import React from 'react';
import { countries } from 'country-data';
import {
  TrackerOutputProps,
  TrackingDetail,
  TrackingStatus,
  TrackerOutputTranslationElementId,
  Icon,
} from './TrackerOutputModels';
import * as cc from '@cimpress/react-components';
import _ from 'lodash'
import { useMediaQuery } from 'react-responsive';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TrackingStatusExtendedDescriptionId,
} from '../../models';
import { Address, StatusCode } from '../TrackerSearchBar/TrackerSearchBarModels';
import {v4 as uuid} from 'uuid';

const TrackerOutput = (props: TrackerOutputProps) => {
  const isTabletOrMobileDevice = useMediaQuery({
    query: '(max-device-width: 1224px)',
  });

  const intl = useIntl();
  const statusCodeDetails = props.componentData.statusCodeMasterData;
  const statusCodeMap = _.indexBy(statusCodeDetails.statusCodes, 'statusCode');

  const formattedData = formatTrackingData(
    props.componentData.trackingStatus.trackingDetails,
    props.componentData.trackingStatus.trackingNumber,
    props.componentData.trackingStatus.shippingAddress || {},
    props.componentData.trackingStatus.carrierName! || '',
    props.componentData.trackingStatus.estimatedDeliveryDate!,
    statusCodeMap,
    intl.locale,
    isTabletOrMobileDevice
  );

  return <>{formattedData}</>;
};

function getStepper(
  trackingDetails: TrackingDetail[],
  trackingNumber: string,
  address: Address,
  carrierName: string,
  estimatedDeliveryDate: string,
  statusCodeMap: Record<string, StatusCode>,
  language: string,
  isTabletOrMobileDevice: boolean
) {
  const headerDiv = getHeaderDiv(
    trackingNumber,
    address,
    carrierName,
    isTabletOrMobileDevice
  );
  const checkMarkIcon = Icon.checkMark;

  const steps = [];

  const isLatestStatusAnException =
    trackingDetails[0].status === TrackingStatus.EXCEPTIONS;
  let activeStepIndex = -1;
  const stepperTextAlignment = isTabletOrMobileDevice ? 'left' : 'center';
  _.sortByOrder(trackingDetails, ['timestamp'], ['asc'])
    .filter(a => a.status !== TrackingStatus.EXCEPTIONS)
    .forEach((detail, index) => {
      const isLastStatus = index === trackingDetails.length - 1;

      steps.push(
        <cc.Step
          key={uuid()}
          overrideBsStyle={'success'}
          icon={
            isLastStatus
              ? detail.status === TrackingStatus.DELIVERED
                ? checkMarkIcon
                : ''
              : checkMarkIcon
          }
        >
          <div style={{ textAlign: stepperTextAlignment }}>
            <div>
              {_.get(
                statusCodeMap,
                `${detail.status}.displayName.${language}`,
                _.get(statusCodeMap, `${detail.status}.displayName.en`, detail.status),
              )}
            </div>
            <small>{convertUtcToLocalTime(detail.timestamp)}</small>
          </div>
        </cc.Step>
      );

      activeStepIndex++;
    });

  const isShipmentDelivered = trackingDetails.some(
    detail => detail.status === TrackingStatus.DELIVERED
  );

  if (isLatestStatusAnException) {
    steps.push(
      <cc.Step overrideBsStyle={'danger'} icon='' key={uuid()}>
        <div style={{ textAlign: stepperTextAlignment }}>
          <div>
          {_.get(statusCodeMap, `EXCEPTIONS.displayName.${language}`, 'EXCEPTIONS')}
          </div>
          <small>{convertUtcToLocalTime(trackingDetails[0].timestamp)}</small>
        </div>
      </cc.Step>
    );
    activeStepIndex++;
  }

  // If the shipment is not delivered and estimated delivery date is present
  // add the step `Estimated Delivery` with estimated delivery date
  // else add empty step `Delivered`
  if (!isShipmentDelivered) {
    const estimatedDelivery =
      (estimatedDeliveryDate && convertUtcToLocalTime(estimatedDeliveryDate)) ||
      '';
    steps.push(
      <cc.Step icon='' key={uuid()}>
        <div style={{ textAlign: stepperTextAlignment }}>
          <div>            
              {
                (estimatedDelivery &&
                  <FormattedMessage id={TrackerOutputTranslationElementId.estimated_delivery} />) ||
                  _.get(statusCodeMap, `DELIVERED.displayName.${language}`, 'DELIVERED')
              }
          </div>

          <small>{estimatedDelivery}</small>
        </div>
      </cc.Step>
    );
  }
  const stepsHeight = isTabletOrMobileDevice
    ? `${steps.length * 65}px`
    : '100px';

  return (
    <div className='container-fluid' style={{ marginTop: '18px' }}>
      <div className='row'>
        <div className='col-md-2'></div>
        <div className='col-md-8'>
          <cc.Card header={headerDiv}>
            <div style={{ height: stepsHeight }}>
              <cc.Stepper
                vertical={isTabletOrMobileDevice}
                activeStep={activeStepIndex}
              >
                {steps}
              </cc.Stepper>
            </div>
          </cc.Card>
          <div className='col-md-2'></div>
        </div>
      </div>
    </div>
  );
}

function getCard(
  cardText: string,
  trackingNumber: string,
  address: Address,
  carrierName: string,
  isTabletOrMobileDevice: boolean
) {
  const headerDiv = getHeaderDiv(
    trackingNumber,
    address,
    carrierName,
    isTabletOrMobileDevice
  );

  return (
    <div className='container-fluid'>
      <div className='row' style={{ marginTop: '18px' }}>
        <div className='col-md-2'></div>
        <div className='col-md-8'>
          <cc.Card header={headerDiv}>
            <div style={{ textAlign: 'left' }} data-set-id='tracking-single-card'>
              <FormattedMessage id={cardText} defaultMessage={cardText} />
            </div>
          </cc.Card>
        </div>
        <div className='col-md-2'></div>
      </div>
    </div>
  );
}

function getHeaderDiv(
  trackingNumber: string,
  address: Address,
  carrierName: string,
  isTabletOrMobileDevice: boolean
) {
  const trackingNumberHeader = (
    <span>
      <FormattedMessage
        id={TrackerOutputTranslationElementId.card_tracking_number_header}
      />
      : <b>{trackingNumber}</b>
    </span>
  );

  const destination = formatAddress(address);
  
  const carrierNameHeader = (
    <span>
      <FormattedMessage id={TrackerOutputTranslationElementId.carrier_name} />:{" "}
      <b>{carrierName}</b>
    </span>
  );

  // TODO: We shall design our application differently in all cases
  // Mobile
  // Tablet
  // Website
  const deliverToHeader = (
    <span>
      <FormattedMessage id={TrackerOutputTranslationElementId.deliver_to} />:{' '}
      <b>{destination}</b>{' '}
      {(countries[address.countryCode!] &&
        countries[address.countryCode!].emoji) ||
        ''}
    </span>
  );

  if (destination.length) {
    return attachToolTip(
      carrierName,
      <div>
        <div className="col-md-4">{carrierNameHeader} </div>
        <div className="col-md-4">{trackingNumberHeader} </div>
        <div className="col-md-4">{deliverToHeader} </div>
      </div>
    );
  }

  return attachToolTip(carrierName, <div>{trackingNumberHeader}</div>);
}

function attachToolTip(content: string, component: JSX.Element) {
  // return (
  //   <cc.Tooltip direction='top' contents={content}>
  //     {component}
  //   </cc.Tooltip>
  // );

  return component;
}

// Format the address in `City, State, Country name` format
// Returns empty when country is missing.
function formatAddress(address: Address) {
  if (!address.countryCode) {
    return '';
  }

  let destination = [];

  if (address.locality) {
    destination.push(address.locality);
  }

  if (address.region) {
    destination.push(address.region);
  }

  destination.push(countries[address.countryCode!].name);

  return destination.join(', ');
}

function formatTrackingData(
  trackingDetails: TrackingDetail[],
  trackingNumber: string,
  address: Address,
  carrierName: string,
  estimatedDeliveryDate: string,
  statusCodeMap: Record<string, StatusCode>,
  language: string,
  isTabletOrMobileDevice: boolean
) {
  /*
    Cases:
      1. Tracking details with more than one element, display Stepper, with or without estimated delivery date.
      2. Tracking details with only one element & has estimated delivery date, display stepper.
      3. Tracking details with one element, display the single card
  */
  if (trackingDetails.length === 1 && !estimatedDeliveryDate) {
    return getSingleCard(
      trackingDetails,
      trackingNumber,
      address,
      carrierName,
      statusCodeMap,
      language,
      isTabletOrMobileDevice
    );
  }

  return getStepper(
    trackingDetails,
    trackingNumber,
    address,
    carrierName,
    estimatedDeliveryDate,
    statusCodeMap,
    language,
    isTabletOrMobileDevice
  );
}

function getSingleCard(
  trackingDetails: TrackingDetail[],
  trackingNumber: string,
  address: Address,
  carrierName: string,
  statusCodeMap: Record<string, StatusCode>,
  language: string,
  isTabletOrMobileDevice: boolean
) {
  const trackingDetail = trackingDetails[0];

  switch (trackingDetail.status) {
    case TrackingStatus.NOT_FOUND:
    case TrackingStatus.TO_BE_IGNORED:
    case TrackingStatus.CANCELLED:
      return getCard(
        TrackingStatusExtendedDescriptionId.not_found,
        trackingNumber,
        address,
        carrierName,
        isTabletOrMobileDevice
      );
    default:
      return getCard(
        _.get(
          statusCodeMap,
          `${trackingDetail.status}.displayName.${language}`,
          _.get(statusCodeMap, `${trackingDetail.status}.displayName.en`, trackingDetail.status),
        ),
        trackingNumber,
        address,
        carrierName,
        isTabletOrMobileDevice
      );
  }
}

/**
 * Converts UTC Timezone to Browser's Local timezone.
 * @param {string} utcTimestamp
 */
function convertUtcToLocalTime(utcTimestamp: string) {
  // Convert UTC time to local time
  let localTime = new Date(utcTimestamp);

  // Returns the browser locale
  // i.e. India > en-GB, US > en-US
  const locale = Intl.DateTimeFormat().resolvedOptions().locale;

  // The time format adopted from the browser locale ,
  // eg: MM/DD/YYYY for US and DD/MM/YYYY for UK

  return Intl.DateTimeFormat(locale).format(localTime);
}

export default TrackerOutput;
